import { create } from 'zustand';
import { ColumnState } from 'ag-grid-community';
import { DateRange } from 'react-day-picker';
import {
	defaultTQC2010_R01InfoDto,
	TQC2010_R01DetailDto,
	TQC2010_R01HeaderDto,
	TQC2010_R01InfoDto,
} from './Dto_TQC2010_R01';
import { getFutureWeekdayExceptTodayAndTomorrow } from 'src/utils/date/dateUtils';

export interface TQC2010_R01Filter {
	compCd: string;
	dateRange: DateRange | undefined;
	workplaceCd: string;
	jobTeam: string;
	workEmp: string;
	workEmpNm: string;
	inspectType: string;
	itemCd: string;
	productOrderNo: string;
}

export interface Store_TQC2010_R01 {
	TQC2010_R01Filter: TQC2010_R01Filter;
	setTQC2010_R01Filter: (filter: Partial<TQC2010_R01Filter>) => void;

	TQC2010_R01List: TQC2010_R01HeaderDto[];
	setTQC2010_R01List: (list: TQC2010_R01HeaderDto[]) => void;

	selectedTQC2010_R01: TQC2010_R01HeaderDto | undefined;
	setSelectedTQC2010_R01: (item: TQC2010_R01HeaderDto | undefined) => void;
	initSelectedTQC2010_R01: () => void;

	editTQC2010_R01: TQC2010_R01InfoDto;
	setEditTQC2010_R01: (item: TQC2010_R01InfoDto) => void;

	TQC2010_R01DetailList: TQC2010_R01DetailDto[];
	setTQC2010_R01DetailList: (list: TQC2010_R01DetailDto[]) => void;
	originalTQC2010_R01DetailList: TQC2010_R01DetailDto[];
	setOriginalTQC2010_R01DetailList: (list: TQC2010_R01DetailDto[]) => void;
	updateTQC2010_R01DetailList: (index: number, data: TQC2010_R01DetailDto) => void;

	detailFetchTrigger: boolean;
	setDetailFetchTrigger: (trigger: boolean) => void;

	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;

	detailColumnState: ColumnState[] | null;
	setDetailColumnState: (state: ColumnState[] | null) => void;

	allCount: number;
	setAllCount: (allCount: number) => void;

	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;

	detailScrollTop: number | null;
	setDetailScrollTop: (top: number | null) => void;

	initData: () => void;
}

export const defaultTQC2010_R01Filter: TQC2010_R01Filter = {
	compCd: '',
	dateRange: {
		from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
		to: getFutureWeekdayExceptTodayAndTomorrow(),
	},
	workplaceCd: '',
	jobTeam: '',
	workEmp: '',
	workEmpNm: '',
	inspectType: '',
	itemCd: '',
	productOrderNo: '',
};

export const useTQC2010_R01Store = create<Store_TQC2010_R01>((set) => ({
	TQC2010_R01Filter: defaultTQC2010_R01Filter,

	setTQC2010_R01Filter: (update) => set((state) => ({
		TQC2010_R01Filter: { ...state.TQC2010_R01Filter, ...update },
	})),

	TQC2010_R01List: [],

	setTQC2010_R01List: (list) => set(() => ({
		TQC2010_R01List: list.map((item, idx) => ({
			...item,
			valueIndex: idx + 1,
			orderIndex: item.orderIndex > -1 ? item.orderIndex : idx + 1,
			orderIndexSeq: item.orderIndexSeq ?? 0,
		})),
	})),

	selectedTQC2010_R01: undefined,
	setSelectedTQC2010_R01: (item) => set({ selectedTQC2010_R01: item }),
	initSelectedTQC2010_R01: () => set({ selectedTQC2010_R01: undefined }),

	editTQC2010_R01: defaultTQC2010_R01InfoDto,
	setEditTQC2010_R01: (item) => set({ editTQC2010_R01: item }),

	TQC2010_R01DetailList: [],
	setTQC2010_R01DetailList: (list) => set(() => ({
		TQC2010_R01DetailList: list.map((item, idx) => ({
			...item,
			valueIndex: idx + 1,
			orderIndex: item.orderIndex > -1 ? item.orderIndex : idx + 1,
			orderIndexSeq: item.orderIndexSeq ?? 0,
			inserted: false,
			isModified: false,
		})),
	})),
	originalTQC2010_R01DetailList: [],
	setOriginalTQC2010_R01DetailList: (list) => set(() => ({
		originalTQC2010_R01DetailList: list.map((item, idx) => ({
			...item,
			valueIndex: idx + 1,
			orderIndex: item.orderIndex > -1 ? item.orderIndex : idx + 1,
			orderIndexSeq: item.orderIndexSeq ?? 0,
			inserted: false,
			isModified: false,
		})),
	})),
	updateTQC2010_R01DetailList: (index, data) => set((state) => ({
		TQC2010_R01DetailList: state.TQC2010_R01DetailList.map((item) => (
			item.valueIndex === index ? data : item
		)),
	})),

	detailFetchTrigger: false,
	setDetailFetchTrigger: (trigger) => set({ detailFetchTrigger: trigger }),

	columnState: null,
	setColumnState: (columnState) => set({ columnState }),

	detailColumnState: null,
	setDetailColumnState: (detailColumnState) => set({ detailColumnState }),

	allCount: 0,
	setAllCount: (allCount) => set({ allCount }),

	scrollTop: null,
	setScrollTop: (scrollTop) => set({ scrollTop }),

	detailScrollTop: null,
	setDetailScrollTop: (detailScrollTop) => set({ detailScrollTop }),

	initData: () => set({
		TQC2010_R01Filter: defaultTQC2010_R01Filter,
		TQC2010_R01List: [],
		selectedTQC2010_R01: undefined,
		editTQC2010_R01: defaultTQC2010_R01InfoDto,
		TQC2010_R01DetailList: [],
		originalTQC2010_R01DetailList: [],
		detailFetchTrigger: false,
		columnState: null,
		detailColumnState: null,
		allCount: 0,
		scrollTop: null,
		detailScrollTop: null,
	}),
}));
